import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { MantineProvider } from '@mantine/core';
import { Notifications, notifications } from '@mantine/notifications';
import { QueryClient, QueryClientProvider, type Mutation } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { theme } from './theme';
import { App } from './App';
import { EditModeProvider } from './context/EditModeContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { initWebVitalsReporter } from './utils/web-vitals-reporter';

import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/charts/styles.css';
import '@mantine/dropzone/styles.css';
import '@mantine/notifications/styles.css';
import './mobile.css';

// Store API URL in localStorage so embedded iframes can access it
localStorage.setItem('apiUrl', import.meta.env.VITE_API_URL || '');

// Initialize Core Web Vitals reporting
initWebVitalsReporter();

// Global error handler for unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  console.error('[Unhandled]', event.reason);
  notifications.show({
    title: 'Something went wrong',
    message: event.reason?.message || 'An unexpected error occurred',
    color: 'red',
    autoClose: 5000,
  });
});

// Show user-visible toast on any failed API mutation
function onMutationError(error: Error, _variables: unknown, _context: unknown, _mutation: Mutation) {
  const msg = error.message || 'Request failed';
  // Skip 401s (handled by auth redirect in api/client.ts)
  if (msg.includes('Unauthorized') || msg.includes('401')) return;

  notifications.show({
    title: 'Action failed',
    message: msg,
    color: 'red',
    autoClose: 5000,
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      // Don't throw on query errors — let components handle with error state
      throwOnError: false,
    },
    mutations: {
      onError: onMutationError as never,
    },
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <MantineProvider theme={theme} defaultColorScheme="auto">
        <Notifications position="top-right" />
        <QueryClientProvider client={queryClient}>
          <ErrorBoundary label="Application">
            <EditModeProvider>
              <App />
            </EditModeProvider>
          </ErrorBoundary>
        </QueryClientProvider>
      </MantineProvider>
    </BrowserRouter>
  </StrictMode>,
);
